import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Search, Copy } from "lucide-react";
import { useCachedList } from "@/lib/cache";
import { TagPicker } from "@/components/tags/TagPicker";
import { matchesTags } from "@/components/tags/TagBadges";
import { InlineTags } from "@/components/tags/InlineTags";

export default function MenuDashboardPage() {
  const [recipesData] = useCachedList("recipes");
  const recipes = recipesData || [];
  const loading = !recipesData;
  const [query, setQuery] = useState("");
  const [tagFilter, setTagFilter] = useState([]);

  const rows = useMemo(() => {
    let r = recipes.filter((x) => x.level === "L3" && matchesTags(x, tagFilter));
    if (query.trim()) r = r.filter((x) => x.name.toLowerCase().includes(query.toLowerCase()));
    return r.sort((a, b) => a.name.localeCompare(b.name));
  }, [recipes, query, tagFilter]);

  const copyAll = () => {
    const header = ["Menu Item", "L2 Components", "COGS", "Tags"].join("\t");
    const body = rows.map((r) => [r.name, r.components.map((c) => `${c.name} ${c.qty}${c.unit}`).join(", "), r.total_cost.toFixed(2), (r.tags || []).join(", ")].join("\t")).join("\n");
    navigator.clipboard.writeText(`${header}\n${body}`);
    toast.success("Copied to clipboard (TSV)");
  };

  return (
    <div className="p-6 md:p-8" data-testid="menu-dashboard-page">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">L3 · Menu Dashboard</h1>
          <p className="mt-1 text-sm text-zinc-500">Final saleable items, their L2 components and fully aggregated COGS.</p>
        </div>
        <button data-testid="copy-menu-button" onClick={copyAll}
          className="inline-flex items-center gap-1.5 rounded-sm border border-zinc-300 bg-white px-3 py-2 text-sm font-medium text-zinc-700 transition-colors hover:bg-zinc-50 no-print">
          <Copy className="h-4 w-4" strokeWidth={1.5} /> Copy grid
        </button>
      </div>

      <div className="mb-3 flex flex-wrap items-center gap-3">
      <div className="flex min-w-0 max-w-md flex-1 basis-64 items-center gap-2 rounded-sm border border-zinc-300 bg-white px-3 py-2">
        <Search className="h-4 w-4 text-zinc-400" strokeWidth={1.5} />
        <input data-testid="menu-search-input" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search menu items…"
          className="w-full border-0 bg-transparent p-0 text-sm focus:outline-none focus:ring-0" />
      </div>
      <TagPicker testid="menu-tag-filter" filter value={tagFilter} onChange={setTagFilter} />
      <span data-testid="menu-filter-result-count" className="text-xs text-zinc-500">{rows.length} menu items</span>
      </div>

      <div data-testid="menu-table-scroll" tabIndex={0} role="region" aria-label="Menu dashboard table" className="overflow-x-auto border border-zinc-300">
        <table data-testid="menu-table" className="w-max min-w-full border-collapse text-left [&_th]:whitespace-nowrap [&_td]:whitespace-nowrap">
          <thead>
            <tr className="bg-zinc-100 text-xs font-semibold uppercase tracking-wider text-zinc-600">
              <th className="border-b border-zinc-300 px-3 py-2 w-64">Menu Item (L3)</th>
              <th className="border-b border-l border-zinc-300 px-3 py-2">L2 Components &amp; Quantities</th>
              <th className="border-b border-l border-zinc-300 px-3 py-2 text-right w-28">Output</th>
              <th className="border-b border-l border-zinc-300 px-3 py-2 text-right w-32">COGS</th>
              <th className="border-b border-l border-zinc-300 px-3 py-2 text-right w-36">COGS / Unit</th>
              <th data-testid="menu-tags-header" className="border-b border-l border-zinc-300 px-3 py-2">Tags</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="px-3 py-8 text-center text-sm text-zinc-400">Loading…</td></tr>}
            {!loading && rows.length === 0 && <tr><td data-testid="menu-filter-empty" colSpan={6} className="px-3 py-8 text-center text-sm text-zinc-400">{query || tagFilter.length ? "No matching menu items." : "No L3 menu items yet. Create them in the Recipe Directory."}</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} className="hover:bg-zinc-50 text-[13px] align-top" data-testid={`menu-row-${r.id}`}>
                <td data-testid={`menu-name-${r.id}`} className="min-w-64 border-b border-zinc-200 px-3 py-2 font-medium">
                  <Link data-testid={`menu-recipe-link-${r.id}`} to={`/recipes/${r.id}`} className="hover:text-blue-600">{r.name}</Link>
                </td>
                <td className="border-b border-l border-zinc-200 px-3 py-2">
                  <div className="flex max-w-lg flex-wrap gap-1.5">
                    {r.components.map((c, i) => (
                      <span key={i} className="inline-flex items-center gap-1 rounded-sm border border-zinc-200 bg-zinc-50 px-2 py-0.5">
                        <span className="text-zinc-700">{c.name}</span>
                        <span className="font-mono text-xs text-zinc-500">{c.qty}{c.unit}</span>
                        <span className="font-mono text-[10px] text-zinc-400">({c.cost_pct.toFixed(0)}%)</span>
                      </span>
                    ))}
                  </div>
                </td>
                <td className="border-b border-l border-zinc-200 px-3 py-2 text-right font-mono">{r.output_qty} {r.output_unit}</td>
                <td className="border-b border-l border-zinc-200 px-3 py-2 text-right font-mono font-semibold" data-testid={`menu-cogs-${r.id}`}>₹{r.total_cost.toFixed(2)}</td>
                <td className="border-b border-l border-zinc-200 px-3 py-2 text-right font-mono">₹{r.unit_cost.toFixed(2)}<span className="text-zinc-400">/{r.output_unit}</span></td>
                <td className="border-b border-l border-zinc-200 px-3 py-2"><InlineTags item={r} testid={`menu-tags-${r.id}`} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
